import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {useLocation, useMatches} from 'react-router';
import {createWebsiteSchema} from '@utils/structuredData';

const SITE_NAME = 'Go Problems';
const SITE_URL = 'https://goproblems.com';
const DEFAULT_TITLE =
    'Go Problems, Tsumego and Baduk Training | GoProblems.com';
const DEFAULT_DESCRIPTION =
    'Solve interactive Go problems, practise tsumego, explore problem collections, track your progress, and improve your Baduk or Weiqi reading skills.';

export type StructuredData = Record<string, unknown>;

type SeoTitleContextValue = {
    pageTitles: Record<string, string>;
    registerPageTitle: (pathname: string, title: string) => void;
};

const SeoTitleContext = createContext<SeoTitleContextValue | null>(null);

export const SeoTitleProvider = ({children}: {children: ReactNode}) => {
    const [pageTitles, setPageTitles] = useState<Record<string, string>>({});
    const value = useMemo<SeoTitleContextValue>(
        () => ({
            pageTitles,
            registerPageTitle: (pathname, title) => {
                setPageTitles(current =>
                    current[pathname] === title
                        ? current
                        : {...current, [pathname]: title}
                );
            },
        }),
        [pageTitles]
    );

    return (
        <SeoTitleContext.Provider value={value}>
            {children}
        </SeoTitleContext.Provider>
    );
};

export const useRegisterSeoTitle = (title: string) => {
    const context = useContext(SeoTitleContext);
    const {pathname} = useLocation();

    useEffect(() => {
        context?.registerPageTitle(pathname, title);
    }, [context, pathname, title]);
};

type SeoProps = {
    title?: string;
    description?: string;
    canonicalPath?: string;
    image?: string;
    type?: 'website' | 'article' | 'profile';
    robots?: string;
    structuredData?: StructuredData | StructuredData[];
    publishedAt?: string;
    modifiedAt?: string;
    author?: string;
};

const absoluteUrl = (value: string): string =>
    new URL(value, SITE_URL).toString();

const serializeStructuredData = (data: StructuredData): string =>
    JSON.stringify(data).replace(/</g, '\\u003c');

export const Seo = ({
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    canonicalPath,
    image,
    type = 'website',
    robots = 'index, follow',
    structuredData,
    publishedAt,
    modifiedAt,
    author,
}: SeoProps) => {
    const {pathname} = useLocation();
    const canonicalUrl = absoluteUrl(canonicalPath ?? pathname);
    const socialImage = image ? absoluteUrl(image) : undefined;
    const schemas = structuredData
        ? Array.isArray(structuredData)
            ? structuredData
            : [structuredData]
        : [];

    useEffect(() => {
        document
            .querySelectorAll('[data-server-seo]')
            .forEach(element => element.remove());
    }, []);

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="robots" content={robots} />
            <link rel="canonical" href={canonicalUrl} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonicalUrl} />
            {socialImage && <meta property="og:image" content={socialImage} />}
            {socialImage && <meta property="og:image:alt" content={title} />}
            <meta
                name="twitter:card"
                content={socialImage ? 'summary_large_image' : 'summary'}
            />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:url" content={canonicalUrl} />
            {socialImage && <meta name="twitter:image" content={socialImage} />}
            {socialImage && (
                <meta name="twitter:image:alt" content={title} />
            )}
            {publishedAt && (
                <meta property="article:published_time" content={publishedAt} />
            )}
            {modifiedAt && (
                <meta property="article:modified_time" content={modifiedAt} />
            )}
            {author && <meta property="article:author" content={author} />}
            {schemas.map((schema, index) => (
                <script key={index} type="application/ld+json">
                    {serializeStructuredData(schema)}
                </script>
            ))}
        </>
    );
};

type RouteSeoConfig = Pick<
    SeoProps,
    'title' | 'description' | 'robots' | 'structuredData'
>;

const STATIC_ROUTES: Record<string, RouteSeoConfig> = {
    '/': {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        structuredData: createWebsiteSchema(),
    },
    '/problems': {
        title: 'Go Problems Database: Tsumego & Go Puzzles | GoProblems.com',
        description:
            'Browse thousands of interactive online Go problems, tsumego, and Go puzzles for Baduk and Weiqi players from beginner to advanced.',
    },
    '/collections': {
        title: 'Go Problem Collections and Tsumego Sets | GoProblems.com',
        description:
            'Study curated collections of tsumego, tesuji, joseki, fuseki, and life-and-death problems.',
    },
    '/collections/progress': {
        title: 'Go Problem Collection Progress | GoProblems.com',
        description:
            'See your progress in each Go problem collection, revisit unfinished tsumego, and find the next puzzles to study.',
    },
    '/srs': {
        title: 'Spaced Repetition for Go Problems | GoProblems.com',
        description:
            'Review Go problems you missed with a spaced repetition schedule, manage your training list, and reinforce your tsumego reading.',
    },
    '/rush': {
        title: 'Go Problem Rush | Go Problems',
        description:
            'Solve Go problems against the clock in Rush mode and improve your speed and reading.',
    },
    '/rushes/leaderboard': {
        title: 'Go Problem Rush Leaderboard | Go Problems',
        description:
            'See the leading Go Problem Rush scores from players in the community.',
    },
    '/time-trials': {
        title: 'Timed Go Problems and Tsumego Challenges | GoProblems.com',
        description:
            'Take timed Go and tsumego challenges by level, race the clock, compare scores, and improve your reading speed.',
    },
    '/time-trials-new': {
        title: 'Timed Go Problems and Tsumego Challenges | GoProblems.com',
        description:
            'Practice timed Go problem sets matched to your level and track your results.',
    },
    '/time-trials/leaderboard': {
        title: 'Time Trial Leaderboard | Go Problems',
        description:
            'Compare the top Go problem time trial results across difficulty levels.',
    },
    '/leaderboard': {
        title: 'Go Players Leaderboard | Go Problems',
        description:
            'Explore the Go Problems community leaderboard and top problem solvers.',
    },
    '/daily-leaderboard': {
        title: 'Daily Go Problem Leaderboard | Go Problems',
        description:
            'See today\'s leading Go problem solvers and daily challenge results.',
    },
    '/tournaments': {
        title: 'Go Problem Tournaments | GoProblems.com',
        description:
            'Join competitive Go problem-solving tournaments and review tournament results.',
    },
    '/invasions': {
        title: 'Go Invasion Problems & Interactive Scenarios | GoProblems.com',
        description:
            'Practice interactive Go invasion problems, reductions, positional judgment, tactical follow-ups, and whole-board decision making.',
    },
    '/groups': {
        title: 'Go Problems Community Groups | Go Problems',
        description:
            'Find groups of Go players, solve problems together, and share progress.',
    },
    '/statistics': {
        title: 'Go Problem Database Statistics | Go Problems',
        description:
            'Explore statistics about problems, attempts, difficulty, and activity on Go Problems.',
    },
    '/download': {
        title: 'Download Go Problems | Go Problems',
        description:
            'Learn how to download and use Go problem resources from Go Problems.',
    },
    '/rss': {
        title: 'Go Problems RSS Feeds | Go Problems',
        description:
            'Subscribe to Go Problems updates and community activity using RSS feeds.',
    },
    '/release-notes': {
        title: 'Go Problems Release Notes | GoProblems.com',
        description:
            'Read the latest GoProblems.com release notes, product improvements, and new features.',
    },
    '/sitemap': {
        title: 'Site Map | Go Problems',
        description: 'Browse the main sections and public pages on Go Problems.',
    },
    '/tools/sgf/difftool': {
        title: 'SGF Diff & Compare Tool – Analyze Go Files | GoProblems.com',
        description:
            'Compare two SGF Go files and inspect differences in moves, properties, markup, and game-tree variations with this free online SGF diff tool.',
    },
    '/site-map': {
        title: 'Site Map | Go Problems',
        description: 'Browse the main sections and public pages on Go Problems.',
    },
};

const NOINDEX_PATHS = [
    /^\/auth(?:\/|$)/,
    /^\/get-pro(?:\/|$)/,
    /^\/user-search(?:\/|$)/,
    /^\/dashboard(?:\/|$)/,
    /^\/settings(?:\/|$)/,
    /^\/notifications(?:\/|$)/,
    /^\/friends(?:\/|$)/,
    /^\/users\/[^/]+\/(?:problem-list|favorite-list|favorites|attempts|comments|achievements)(?:\/|$)/,
    /^\/friendship-requests(?:\/|$)/,
    /^\/potd\/streaks(?:\/|$)/,
    /^\/analysis(?:\/|$)/,
    /^\/under-construction(?:\/|$)/,
    /^\/rushes\/(?:\d+|live)(?:\/|$)/,
    /^\/time-trials\/\d+(?:\/|$)/,
    /^\/collection-tests(?:\/|$)/,
    /^\/training(?:\/|$)/,
    /^\/srs(?:\/|$)/,
    /^\/scenario-cases(?:\/|$)/,
    /^\/scenarios\/[^/]+\/cases(?:\/|$)/,
    /^\/suggestions(?:\/|$)/,
    /^\/curations(?:\/|$)/,
    /^\/problem-drafts(?:\/|$)/,
    /^\/achievements(?:\/|$)/,
    /^\/comments\/recent(?:\/|$)/,
    /^\/levels(?:\/|$)/,
    /^\/groups\/\d+(?:\/|$)/,
    /^\/invasions\/\d+(?:\/|$)/,
    /^\/problems\/(?:filter|next|practice|recommendations|search|flagged|need-rating|lint-issues|create)(?:\/|$)/,
    /^\/problems\/[^/]+\/(?:edit|research|paths-history|rating-history|potd-attempts|my-attempts|edit-history|flaghist|cleared-flags)(?:\/|$)/,
    /\/(?:create|edit|result|results|attempts|play|lobby)(?:\/|$)/,
];

const titleCase = (value: string): string =>
    value
        .split('-')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const dynamicRouteSeo = (pathname: string): RouteSeoConfig => {
    const authorProblemsMatch = pathname.match(
        /^\/users\/([^/]+)\/problems\/?$/
    );
    if (authorProblemsMatch) {
        const username = decodeURIComponent(authorProblemsMatch[1]);
        return {
            title: `${username} Go Problems & Tsumego | GoProblems.com`,
            description: `Solve Go problems and tsumego created by ${username} in the GoProblems.com interactive problem database.`,
        };
    }

    const favoriteProblemsMatch = pathname.match(
        /^\/users\/([^/]+)\/favorites\/?$/
    );
    if (favoriteProblemsMatch) {
        const username = decodeURIComponent(favoriteProblemsMatch[1]);
        return {
            title: `${username}'s Favorite Go Problems | GoProblems.com`,
            description: `Browse Go problems saved by ${username}.`,
        };
    }

    const problemMatch = pathname.match(/^\/problems\/(\d+)$/);
    if (problemMatch) {
        return {
            title: `Go Problem #${problemMatch[1]} | Go Problems`,
            description: `Solve interactive Go problem #${problemMatch[1]} and compare your result with the Go Problems community.`,
        };
    }

    const profileMatch = pathname.match(/^\/users\/([^/]+)$/);
    if (profileMatch) {
        const username = decodeURIComponent(profileMatch[1]);
        return {
            title: `${username}'s Go Profile | Go Problems`,
            description: `View ${username}'s public Go Problems profile, solving activity, created problems, and achievements.`,
        };
    }

    if (pathname.startsWith('/article/')) {
        return {
            title: 'Go Problems Guide | Go Problems',
            description:
                'Read guides and information about solving, creating, and studying Go problems.',
        };
    }

    if (pathname.startsWith('/collections/')) {
        return {
            title: 'Go Problem Collection | Go Problems',
            description:
                'Study a curated collection of interactive Go problems and track your progress.',
        };
    }

    if (pathname.startsWith('/tournaments/')) {
        return {
            title: 'Go Problem Tournament | Go Problems',
            description:
                'View a Go problem-solving tournament, its rules, players, and results.',
        };
    }

    if (pathname.startsWith('/invasions/')) {
        return {
            title: 'Go Invasion Scenario | Go Problems',
            description:
                'Play and study an interactive Go invasion scenario on Go Problems.',
        };
    }

    const lastSegment = pathname.split('/').filter(Boolean).at(-1) ?? 'Page';
    return {
        title: `${titleCase(lastSegment)} | Go Problems`,
        description:
            'Practice and study interactive Go, baduk, and weiqi problems with the Go Problems community.',
    };
};

export const RouteSeo = () => {
    const {pathname} = useLocation();
    const matches = useMatches();
    const titleContext = useContext(SeoTitleContext);
    const pageOwnsSeo =
        /^\/problems\/\d+$/.test(pathname) ||
        /^\/collections\/\d+$/.test(pathname) ||
        /^\/users\/[^/]+$/.test(pathname) ||
        pathname.startsWith('/article/') ||
        /^\/tournaments\/\d+\/(?:lobby|results|rules)$/.test(pathname);

    if (pageOwnsSeo) {
        return null;
    }

    const config = STATIC_ROUTES[pathname] ?? dynamicRouteSeo(pathname);
    const title = titleContext?.pageTitles[pathname] ?? config.title;
    const indexableTournamentPage =
        /^\/tournaments\/\d+\/(?:lobby|results|rules)$/.test(pathname);
    const noindex =
        !indexableTournamentPage &&
        ((pathname !== '/' &&
            NOINDEX_PATHS.some(pattern => pattern.test(pathname))) ||
            matches.some(
                match =>
                    (match.handle as {noindex?: boolean} | undefined)
                        ?.noindex === true
            ));
    return (
        <Seo
            {...config}
            title={title}
            robots={noindex ? 'noindex, follow' : config.robots}
        />
    );
};
